import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  PhoneFrame,
  NarrativeViewer,
  NarrativeNotFound,
  NarrativeAccessGate,
  AudioProvider,
  BookmarkProvider,
  UserInputProvider,
  useNarrativeStructure,
} from "@juiceforward/narrative-player";
import type { DeviceType } from "@juiceforward/narrative-player";
import { LandingPage } from "./components/LandingPage";
import { UseCasePage } from "./components/use-cases/UseCasePage";
import { getUseCaseConfig } from "./use-cases/registry";
import { AdminPage } from "./components/admin/AdminPage";
import { SessionProvider } from "./contexts/SessionContext";
import { EmailExportModal } from "./components/EmailExportModal";
import {
  getNarrativeByReportId,
  fetchRemoteNarrative,
  fetchStaticNarrative,
  getManifestForReportId,
  getAllManifests,
} from "./narratives";
import type {
  NarrativeEntry,
  PipelineManifest,
  PipelineManifestInfo,
} from "./narratives";
import { DevNavigator, DevNavigatorToggle } from "./components/DevNavigator";
import {
  fetchShareTokenNarrative,
  recordSessionAction,
  fetchCampaignRuns,
  fetchCampaignRunManifest,
  dispatchManifestToPipeline,
  labelCampaignRuns,
  sortCampaignRuns,
  formatRunDate,
  workspaceOptions,
} from "./utils/fetchShareToken";
import type { CampaignRunInfo } from "./utils/fetchShareToken";
import {
  getShareTokenSession,
  setShareTokenSession,
} from "./utils/shareTokenSession";

/**
 * URL Parameter handling:
 * - `report`: Maps to narrative's metadata.report_id, or a full URL for remotely-hosted narratives
 * - `p`: Page number (group index) for deep linking and refresh persistence
 * - `format`: Rendering mode (e.g., "video" for auto-advance)
 * - `admin`: Shows admin dashboard
 * - `token`: Access token required for narratives protected by metadata.access_token.
 *            For remote narratives, auth is embedded in the URL itself (e.g. signed URLs).
 *
 * Example: ?report=report_67890&p=5
 * Example: ?report=report_67890&token=my-secret-token
 * Example: ?report=https://raw.githubusercontent.com/juiceinc/repo/sha/path.json?token=GHSAT...
 * Example: ?share=aac69dfb-d34c-407a-a586-0ff4b98a9a92
 * Example: ?admin (for admin dashboard)
 */
function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const rawVideoDelayMs = params.get("videoDelayMs");
  const videoDelayMs =
    rawVideoDelayMs !== null && /^\d+$/.test(rawVideoDelayMs)
      ? Number(rawVideoDelayMs)
      : null;
  const rawRun = params.get("run");
  const run = rawRun !== null && /^\d+$/.test(rawRun) ? Number(rawRun) : null;

  return {
    reportId: params.get("report"),
    shareToken: params.get("share"),
    page: params.get("p"),
    format: params.get("format"),
    videoDelayMs,
    admin: params.has("admin"),
    token: params.get("token"),
    devnav: params.has("devnav"),
    device: params.get("device") as DeviceType | null,
    // Devnav only: the Dispatch campaign run the share token belongs to, so the
    // review panel opens on that run instead of the first one listed.
    run,
  };
}

/**
 * Path-based routes served by the SPA. We do path matching ourselves (no
 * router library) — nginx and the Vite dev server both fall back to
 * index.html for unknown paths, so any /use-cases/* request reaches React.
 *
 * `/use-cases/<slug>` is matched against the use-case registry; unknown
 * slugs fall through and the app renders its normal narrative/landing
 * routing.
 */
function getPathRoute(): { kind: "use-case"; slug: string } | null {
  const path = window.location.pathname.replace(/\/+$/, "");
  const useCaseMatch = path.match(/^\/use-cases\/([^/]+)$/);
  if (useCaseMatch && getUseCaseConfig(useCaseMatch[1])) {
    return { kind: "use-case", slug: useCaseMatch[1] };
  }
  return null;
}

function updateUrlDevNav(open: boolean) {
  const params = new URLSearchParams(window.location.search);
  if (open) {
    params.set("devnav", "");
  } else {
    params.delete("devnav");
  }
  window.history.replaceState({}, "", `?${params.toString()}`);
}

function updateUrlRun(runId: number) {
  const params = new URLSearchParams(window.location.search);
  params.set("run", String(runId));
  window.history.replaceState({}, "", `?${params.toString()}`);
}

/**
 * Drop the review flow's params, leaving the share link (and page) a
 * recipient would have. Used when `?devnav` is opened without a Dispatch
 * session, so the URL stops advertising a panel that can't load.
 */
function stripUrlDevNavParams() {
  const params = new URLSearchParams(window.location.search);
  params.delete("devnav");
  params.delete("run");
  params.delete("device");
  const query = params.toString();
  window.history.replaceState({}, "", query ? `?${query}` : "/");
}

function updateUrlPage(page: number) {
  const params = new URLSearchParams(window.location.search);
  params.set("p", String(page));
  window.history.replaceState({}, "", `?${params.toString()}`);
}

function updateUrlDevice(device: DeviceType) {
  const params = new URLSearchParams(window.location.search);
  params.set("device", device);
  window.history.replaceState({}, "", `?${params.toString()}`);
}

/**
 * NarrativeApp handles narrative display with all React hooks.
 * Separated from App to allow routing without violating hooks rules.
 */
function NarrativeApp({
  reportId,
  shareToken,
  page,
  format,
  videoDelayMs,
  token,
  devnav,
  deviceType,
  showEmailExport,
  onEmailExportClose,
}: {
  reportId: string | null;
  shareToken: string | null;
  page: string | null;
  format: string | null;
  videoDelayMs: number | null;
  token: string | null;
  /**
   * The internal review flow (`?devnav`). On the share path this sends the
   * reviewer's Dispatch session with the query, so a signed-in Juicer gets
   * Dispatch's claim exemption instead of claiming each bound token opened.
   */
  devnav: boolean;
  deviceType: DeviceType;
  showEmailExport?: boolean;
  onEmailExportClose?: () => void;
}) {
  // Remote narrative state (used when reportId is a URL)
  const isRemote =
    !!reportId &&
    (reportId.startsWith("http://") || reportId.startsWith("https://"));
  const isShare = !!shareToken;
  const [asyncEntry, setAsyncEntry] = useState<
    NarrativeEntry | null | undefined
  >(
    isRemote || isShare ? undefined : null, // undefined = loading, null = not found / error
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  // A share-token denial we can name. `verificationRequired` is deliberately
  // absent: until the re-verify screen exists it is indistinguishable to the
  // recipient from "no such token", which already renders NarrativeNotFound.
  const [shareDenial, setShareDenial] = useState<
    "expired" | "tierUnsupported" | null
  >(null);

  // Sync lookup from bundled registry (fast path)
  const bundledEntry =
    !isRemote && reportId ? getNarrativeByReportId(reportId) : null;

  // Fetch remote narrative when reportId is a URL
  useEffect(() => {
    if (!isRemote || !reportId) return;
    let active = true;
    setAsyncEntry(undefined);
    fetchRemoteNarrative(reportId).then((entry) => {
      if (active) setAsyncEntry(entry);
    });
    return () => {
      active = false;
    };
  }, [isRemote, reportId]);

  // Fetch narrative via share token from GraphQL API.
  //
  // The session id stored for this token is replayed on the way in and the
  // freshly issued one written back on the way out, so a reload, a back button
  // or a self-embedded demo iframe presents the same credential rather than
  // arriving as a stranger.
  //
  // Under `?devnav` the query goes to Dispatch with the reviewer's session, so
  // a signed-in Juicer walking a campaign run gets Dispatch's internal
  // exemption instead of claiming each bound token they open.
  useEffect(() => {
    if (!isShare || !shareToken) return;
    let active = true;
    setAsyncEntry(undefined);
    setSessionId(null); // Reset to prevent stale session tracking
    setShareDenial(null);
    const storedSessionId = getShareTokenSession(shareToken);
    fetchShareTokenNarrative(shareToken, storedSessionId, {
      viaDispatchSession: devnav,
    }).then((result) => {
      // Persist before the liveness check, not after. If this effect was torn
      // down while the request was in flight — StrictMode's double-invoke in
      // dev, a devnav switch to another token in prod — and this request is
      // the one that claimed a bound token, dropping its sessionId here would
      // strand the claim: no browser would hold the credential, this one
      // included, and the link would be dead for everyone.
      if (result.kind === "ok") {
        setShareTokenSession(shareToken, result.sessionId);
      }
      if (!active) return;
      if (result.kind === "ok") {
        setAsyncEntry(result.entry);
        setSessionId(result.sessionId);
        return;
      }
      setAsyncEntry(null);
      setSessionId(null);
      if (result.kind === "expired" || result.kind === "tierUnsupported") {
        setShareDenial(result.kind);
      }
    });
    return () => {
      active = false;
    };
  }, [isShare, shareToken, devnav]);

  // Fetch static pipeline narrative when not bundled but known to a manifest
  useEffect(() => {
    if (isShare || isRemote || !reportId || bundledEntry) return;
    let active = true;
    setAsyncEntry(undefined);
    fetchStaticNarrative(reportId).then((entry) => {
      if (active) setAsyncEntry(entry ?? null);
    });
    return () => {
      active = false;
    };
  }, [isShare, isRemote, reportId, bundledEntry]);

  // Resolve narrative entry: share > bundled > async (remote or static)
  const narrativeEntry = isShare
    ? asyncEntry
    : (bundledEntry ?? (isRemote || !bundledEntry ? asyncEntry : null));

  const isLoading = narrativeEntry === undefined;

  const data = narrativeEntry?.narrative;

  // Check access token gate for bundled narratives
  // Note: remote/share narratives are protected via auth embedded in the URL/token itself.
  const requiredToken =
    !isRemote && !isShare ? data?.metadata?.access_token : undefined;
  const tokenMissing = !!requiredToken && !token;
  const tokenInvalid = !!requiredToken && !!token && token !== requiredToken;

  // Parse initial page from URL (will be clamped in NarrativeViewer)
  const initialPage = page ? parseInt(page, 10) : 0;

  // Callback to sync URL when page changes
  const handlePageChange = useCallback(
    (pageIndex: number) => {
      updateUrlPage(pageIndex);
      if (sessionId) {
        recordSessionAction(sessionId, "pageChange", { page: pageIndex });
      }
    },
    [sessionId],
  );

  // Determine if table_of_contents has selectSections unset or false
  const shouldAutoSelectAll = useMemo(() => {
    if (!data) return true;

    const tocSection = data.sections.find((s) => s.id === "table_of_contents");
    if (!tocSection) return true; // If no TOC section, auto-select all

    const tocGroup = tocSection.groups[0];
    if (!tocGroup) return true;

    const tocItem = tocGroup.items.find(
      (item) => item.type === "table_of_contents",
    );
    if (!tocItem) return true; // If no TOC item, auto-select all

    // selectSections defaults to false (read-only TOC), so auto-select all
    // unless it is explicitly true
    return !tocItem.selectSections;
  }, [data]);

  // Get all selectable section IDs (exclude intro and table_of_contents)
  const selectableSectionIds = useMemo(() => {
    if (!data) return [];

    return data.sections
      .filter(
        (s) => s.label && s.id !== "intro" && s.id !== "table_of_contents",
      )
      .map((s) => s.id);
  }, [data]);

  // A deep link (?p=N) means the viewer already passed the table of contents
  // in an earlier visit. Select every section so the linked page exists and
  // the position survives a refresh instead of being clamped to the TOC.
  // Captured once at mount so later URL page updates don't flip the default.
  const [hasDeepLink] = useState(() => initialPage > 0);

  // Default selection: all sections if auto-select (or deep-linked), otherwise
  // empty. Derived synchronously from the loaded data so an async narrative
  // never mounts the viewer with a stale, empty selection (which would clamp
  // the initial page). User toggles override the default for that narrative.
  const defaultSelectedSections = useMemo(
    () =>
      shouldAutoSelectAll || hasDeepLink
        ? new Set(selectableSectionIds)
        : new Set<string>(),
    [shouldAutoSelectAll, hasDeepLink, selectableSectionIds],
  );
  const [userSelection, setUserSelection] = useState<{
    forData: typeof data;
    sections: Set<string>;
  } | null>(null);
  const selectedSections =
    userSelection && userSelection.forData === data
      ? userSelection.sections
      : defaultSelectedSections;

  // Email export modal controlled by parent (DevNavigator trigger)
  const emailExportOpen = showEmailExport ?? false;

  // Calculate total groups for PDF generation (must be called before conditional returns)
  const { totalGroups } = useNarrativeStructure(data, selectedSections);

  const handleSectionToggle = (sectionId: string, isSelected: boolean) => {
    setUserSelection((previous) => {
      const baseSections =
        previous && previous.forData === data
          ? previous.sections
          : selectedSections;
      const next = new Set(baseSections);
      if (isSelected) {
        next.add(sectionId);
      } else {
        next.delete(sectionId);
      }
      return { forData: data, sections: next };
    });
  };

  // Show landing page if no report ID or share token is provided
  if (!reportId && !shareToken) {
    return <LandingPage />;
  }

  // Show loading state while fetching remote narrative
  if (isLoading) {
    return (
      <PhoneFrame format={format}>
        <div className="flex h-full w-full items-center justify-center bg-slate-900 text-white/50 text-sm">
          Loading…
        </div>
      </PhoneFrame>
    );
  }

  // Show access gate if token is missing or invalid for a bundled narrative
  if (tokenMissing || tokenInvalid) {
    return (
      <PhoneFrame format={format}>
        <NarrativeAccessGate tokenInvalid={tokenInvalid} />
      </PhoneFrame>
    );
  }

  // Share links used to bypass the gate entirely and fall through to
  // "Report Not Found" for every failure. Now that Dispatch names the outcome,
  // the two we can act on get the gate instead.
  if (shareDenial) {
    return (
      <PhoneFrame format={format}>
        <NarrativeAccessGate
          reason={shareDenial === "expired" ? "expired" : "denied"}
        />
      </PhoneFrame>
    );
  }

  // Handle narrative not found
  if (!narrativeEntry || !data) {
    return (
      <PhoneFrame format={format}>
        <NarrativeNotFound requestedReportId={reportId ?? shareToken} />
      </PhoneFrame>
    );
  }

  const { dataValues: sampleDataValues } = narrativeEntry;

  return (
    <>
      <PhoneFrame format={format} deviceType={deviceType}>
        <SessionProvider sessionId={sessionId}>
          <AudioProvider>
            <BookmarkProvider>
              <UserInputProvider>
                <NarrativeViewer
                  narrativeData={data}
                  dataValues={sampleDataValues}
                  selectedSections={selectedSections}
                  onSectionToggle={handleSectionToggle}
                  initialGroupIndex={initialPage}
                  onGroupIndexChange={handlePageChange}
                  format={format}
                  videoDelayMs={videoDelayMs}
                />
              </UserInputProvider>
            </BookmarkProvider>
          </AudioProvider>
        </SessionProvider>
      </PhoneFrame>

      <EmailExportModal
        isOpen={emailExportOpen}
        onClose={() => onEmailExportClose?.()}
        reportId={data.metadata.report_id}
        narrativeUrl={
          shareToken
            ? `/?share=${shareToken}`
            : `/?report=${data.metadata.report_id}`
        }
        totalGroups={totalGroups}
        shareToken={shareToken ?? undefined}
      />
    </>
  );
}

/**
 * App handles top-level routing without hooks.
 * Routes to LoginPage, AdminPage, or NarrativeApp.
 */
function App() {
  const {
    reportId,
    shareToken,
    page,
    format,
    videoDelayMs,
    admin,
    token,
    devnav: devnavParam,
    device,
    run,
  } = getUrlParams();
  const pathRoute = getPathRoute();
  const [currentReportId, setCurrentReportId] = useState(reportId);
  const [currentShareToken, setCurrentShareToken] = useState(shareToken);
  // Held in state rather than re-read from the URL: closing the panel removes
  // `devnav` from the URL too, and the review flow should end only when we
  // decide it can't work (no Dispatch session), not as a side effect of that.
  const [devnav, setDevnav] = useState(devnavParam);
  // The share query's credential channel is fixed at load. Dropping the review
  // flow mid-visit must not refetch (and re-session) the narrative on screen.
  const [shareViaDispatchSession] = useState(devnavParam);
  // The run named by the link, applied once when the run list first arrives.
  const [linkedRunId] = useState(run);
  const [isDevNavOpen, setIsDevNavOpen] = useState(devnavParam);
  const [deviceType, setDeviceType] = useState<DeviceType>(device ?? "desktop");
  const [devNavSearch, setDevNavSearch] = useState("");
  const [manifestOverride, setManifestOverride] =
    useState<PipelineManifest | null>(null);
  const [showEmailExport, setShowEmailExport] = useState(false);

  // Dispatch-backed review panel state (only populated when ?devnav is present
  // and the user is authenticated to the dispatch backend).
  const [dispatchRuns, setDispatchRuns] = useState<CampaignRunInfo[]>([]);
  const [dispatchManifests, setDispatchManifests] = useState<
    Record<number, PipelineManifest>
  >({});
  const [activeDispatchRunId, setActiveDispatchRunId] = useState<number | null>(
    null,
  );
  const [loadingDispatchRunId, setLoadingDispatchRunId] = useState<
    number | null
  >(null);
  // Per-run fetch failures, shown in the panel for the active run. Kept per
  // run so switching away and back doesn't lose (or wrongly show) a message.
  const [dispatchManifestErrors, setDispatchManifestErrors] = useState<
    Record<number, string>
  >({});
  const [manifestRetryNonce, setManifestRetryNonce] = useState(0);
  // Workspace filter for the run switcher: a stringified workspace id or "all".
  // Only shown when the runs span more than one workspace.
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string>("all");
  // Set when the reviewer deliberately switches run (dropdown or workspace
  // filter); consumed by the effect that navigates into the new run.
  const pendingRunNavigate = useRef(false);

  // Devnav is strictly gated on the ?devnav URL param. Until the user opts in
  // we don't read localStorage or look up local manifests at all.
  const isShareFlow = !!currentShareToken;
  // Admin-auth is only a gate for the local (non-share) pipeline flow; the
  // dispatch-backed share flow relies on the server's own permission checks.
  const isAdminAuthed =
    devnav && !isShareFlow
      ? localStorage.getItem("forward_admin_auth") === "true"
      : false;
  const autoManifest =
    devnav && !isShareFlow && isAdminAuthed && currentReportId
      ? getManifestForReportId(currentReportId)
      : null;
  const localAllManifests = useMemo(
    () => (devnav && !isShareFlow && isAdminAuthed ? getAllManifests() : []),
    [devnav, isShareFlow, isAdminAuthed],
  );

  // Build the list passed to the run-switcher dropdown. We include every run
  // returned by `campaignRuns`, using a cached manifest if we've fetched one
  // or a placeholder stub otherwise so the user can still select it and
  // trigger an on-demand fetch. The numeric run id (as `key`) is the join
  // key: run names are not unique, and a fetched manifest's `runId` is the
  // server's identifier rather than the name, so matching on either left the
  // dropdown stuck on its first option once a run had loaded.
  const dispatchStubManifests = useMemo(() => {
    const stubs: Record<number, PipelineManifest> = {};
    for (const run of dispatchRuns) {
      stubs[run.id] = { runId: run.name, entries: [] };
    }
    return stubs;
  }, [dispatchRuns]);

  const dispatchWorkspaces = useMemo(
    () => workspaceOptions(dispatchRuns),
    [dispatchRuns],
  );

  // The runs the switcher lists: all of them, or one workspace's. Sort order
  // (reviewable first) is inherited from `dispatchRuns`.
  const visibleDispatchRuns = useMemo(
    () =>
      activeWorkspaceId === "all"
        ? dispatchRuns
        : dispatchRuns.filter(
            (r) => String(r.workspaceId) === activeWorkspaceId,
          ),
    [dispatchRuns, activeWorkspaceId],
  );

  // Labels are computed over the visible runs only, so a list filtered to one
  // workspace doesn't qualify every run with that workspace's name.
  const dispatchRunLabels = useMemo(
    () => labelCampaignRuns(visibleDispatchRuns),
    [visibleDispatchRuns],
  );

  const dispatchAllManifests: PipelineManifestInfo[] = useMemo(() => {
    if (!devnav || !isShareFlow) return [];
    return visibleDispatchRuns.map((run) => ({
      project: "dispatch",
      runId: run.name,
      key: String(run.id),
      label: dispatchRunLabels.get(run.id) ?? run.name,
      manifest: dispatchManifests[run.id] ?? dispatchStubManifests[run.id],
    }));
  }, [
    dispatchRunLabels,
    devnav,
    isShareFlow,
    visibleDispatchRuns,
    dispatchManifests,
    dispatchStubManifests,
  ]);

  // While a selected run's manifest is in flight, fall back to its stub so
  // the panel stays mounted and the run dropdown reflects the new selection
  // (the loading overlay covers the empty table in that gap).
  const dispatchActiveManifest =
    activeDispatchRunId != null
      ? (dispatchManifests[activeDispatchRunId] ??
        dispatchStubManifests[activeDispatchRunId] ??
        null)
      : null;

  const manifest =
    manifestOverride ?? (isShareFlow ? dispatchActiveManifest : autoManifest);
  const allManifests = isShareFlow ? dispatchAllManifests : localAllManifests;
  const navigableCount = manifest
    ? manifest.entries.filter(
        (e) =>
          e.metadata?.report_id &&
          e.file !== "(excluded)" &&
          e.file !== "(error)",
      ).length
    : 0;
  const isManifestLoading = isShareFlow && loadingDispatchRunId != null;

  // DevNavigator highlights the "current" entry via metadata.report_id, so in
  // share mode we pass the active share token as the current id (the dispatch
  // manifest synthesises report_id = shareToken for each entry).
  const currentDevNavId = isShareFlow ? currentShareToken : currentReportId;

  const handleNavigate = useCallback(
    (newId: string) => {
      const params = new URLSearchParams(window.location.search);
      if (isShareFlow) {
        params.delete("report");
        params.set("share", newId);
        setCurrentShareToken(newId);
      } else {
        params.set("report", newId);
        setCurrentReportId(newId);
      }
      // Preserve p param and devnav state
      window.history.replaceState({}, "", `?${params.toString()}`);
    },
    [isShareFlow],
  );

  // Shared helper: once a dispatch manifest is ready and active, navigate to
  // its first usable entry (needed after a run-switch so the viewer doesn't
  // stay on a stale share token from a different run).
  const navigateToFirstDispatchEntry = useCallback(
    (manifestToUse: PipelineManifest) => {
      const first = manifestToUse.entries.find(
        (e) =>
          e.metadata?.report_id &&
          e.file !== "(excluded)" &&
          e.file !== "(error)",
      );
      if (first?.metadata?.report_id) {
        handleNavigate(first.metadata.report_id);
      }
    },
    [handleNavigate],
  );

  const handleManifestChange = useCallback(
    (info: PipelineManifestInfo) => {
      setDevNavSearch("");
      if (isShareFlow) {
        // Dispatch entries carry the numeric run id as `key`; the manifest
        // itself may be a cached real manifest or an unfetched stub.
        const runId = Number(info.key);
        if (
          !Number.isFinite(runId) ||
          !dispatchRuns.some((r) => r.id === runId)
        ) {
          return;
        }
        pendingRunNavigate.current = true;
        setActiveDispatchRunId(runId);
        // Fetch (if needed) + auto-navigate are handled by the effects below.
        return;
      }
      setManifestOverride(info.manifest);
      navigateToFirstDispatchEntry(info.manifest);
    },
    [isShareFlow, dispatchRuns, navigateToFirstDispatchEntry],
  );

  const handleSetDeviceType = useCallback((type: DeviceType) => {
    setDeviceType(type);
    updateUrlDevice(type);
  }, []);

  const handleSetDevNavOpen = (open: boolean) => {
    setIsDevNavOpen(open);
    updateUrlDevNav(open);
    if (open && !device) {
      handleSetDeviceType("phone");
    } else if (!open && !device) {
      handleSetDeviceType("desktop");
    }
  };

  // Pull campaign run metadata out of the share token response so we can load
  // the review panel. Only wired up when devnav is explicitly requested.
  // (No-op now that campaign info comes from a separate query — retained as a
  // hook point in case we need token-scoped metadata later.)

  // Fetch the list of campaign runs visible to the authenticated user.
  useEffect(() => {
    if (!devnav || !isShareFlow) return;
    let active = true;
    fetchCampaignRuns().then((result) => {
      if (!active) return;
      if (result.kind === "unauthenticated") {
        // No Dispatch session: the panel can't load, so leave the reviewer on
        // the plain share link rather than a URL implying a review flow.
        stripUrlDevNavParams();
        setDevnav(false);
        setIsDevNavOpen(false);
        setDeviceType("desktop");
        return;
      }
      if (result.kind !== "ok") return;
      // Reviewable runs first, expired ones last (and labeled as such).
      const sorted = sortCampaignRuns(result.runs);
      setDispatchRuns(sorted);
      if (sorted.length === 0) return;
      // Open on the run the link names (Dispatch's "Open Narrative Review"
      // sets it), filtered to its workspace. Otherwise fall back to the first
      // run; the sort makes that the first run that can actually be opened.
      const linked = sorted.find((r) => r.id === linkedRunId);
      if (linked?.workspaceId != null) {
        setActiveWorkspaceId(String(linked.workspaceId));
      }
      setActiveDispatchRunId((cur) => cur ?? (linked ?? sorted[0]).id);
    });
    return () => {
      active = false;
    };
  }, [devnav, isShareFlow, linkedRunId]);

  // Fetch only the active run's manifest on demand — additional runs are
  // fetched lazily when the user selects them from the dropdown.
  useEffect(() => {
    if (!devnav || !isShareFlow) return;
    if (activeDispatchRunId == null) return;
    // A cached hit still clears the loading marker: switching here while
    // another run's fetch is in flight tears that fetch down before it can.
    if (activeDispatchRunId in dispatchManifests) {
      setLoadingDispatchRunId(null);
      return;
    }
    let active = true;
    const runId = activeDispatchRunId;
    setLoadingDispatchRunId(runId);
    fetchCampaignRunManifest(runId).then((result) => {
      if (!active) return;
      setLoadingDispatchRunId((cur) => (cur === runId ? null : cur));
      if (result.kind === "error") {
        setDispatchManifestErrors((prev) => ({
          ...prev,
          [runId]: result.message,
        }));
        return;
      }
      setDispatchManifestErrors((prev) => {
        if (!(runId in prev)) return prev;
        const next = { ...prev };
        delete next[runId];
        return next;
      });
      setDispatchManifests((prev) => ({
        ...prev,
        [runId]: dispatchManifestToPipeline(result.manifest),
      }));
    });
    return () => {
      active = false;
    };
    // `manifestRetryNonce` is a dependency on purpose: bumping it is how the
    // panel's Retry button re-runs a fetch that previously failed.
  }, [
    devnav,
    isShareFlow,
    activeDispatchRunId,
    dispatchManifests,
    manifestRetryNonce,
  ]);

  const handleRetryManifest = useCallback(() => {
    setManifestRetryNonce((n) => n + 1);
  }, []);

  // Narrowing the workspace filter can hide the active run; when it does,
  // move to the first run that is still listed (reviewable runs sort first).
  useEffect(() => {
    if (!isShareFlow || visibleDispatchRuns.length === 0) return;
    if (visibleDispatchRuns.some((r) => r.id === activeDispatchRunId)) return;
    // Only a deliberate filter change lands here with a run already active;
    // the very first selection (no active run yet) must stay put.
    if (activeDispatchRunId != null) pendingRunNavigate.current = true;
    setActiveDispatchRunId(visibleDispatchRuns[0].id);
  }, [isShareFlow, visibleDispatchRuns, activeDispatchRunId]);

  const activeManifestError =
    isShareFlow && activeDispatchRunId != null
      ? dispatchManifestErrors[activeDispatchRunId]
      : undefined;

  // An expired run still lists its entries (useful for seeing who was in it),
  // but every share link in it now resolves to EXPIRED, so say so up front
  // rather than letting each click land on "Report Not Found".
  const activeManifestWarning = useMemo(() => {
    if (!isShareFlow || activeDispatchRunId == null) return undefined;
    const run = dispatchRuns.find((r) => r.id === activeDispatchRunId);
    if (!run?.isExpired) return undefined;
    const when = formatRunDate(run.expiresAt);
    return `This run expired${when ? ` on ${when}` : ""}. Its share links no longer open, so entries below will show "Report Not Found" until the campaign is renewed in Dispatch.`;
  }, [isShareFlow, activeDispatchRunId, dispatchRuns]);

  // After a run switch the reviewer asked for, once the new run's manifest is
  // in hand, navigate to its first usable entry — unless the current share
  // token is already in it. The switch must be deliberate (dropdown or
  // workspace filter): the initial auto-selection of the first listed run
  // must not navigate away from the narrative the reviewer opened, which
  // also spent a share-token fetch and a session on every page load.
  useEffect(() => {
    if (!isShareFlow || activeDispatchRunId == null) return;
    const mf = dispatchManifests[activeDispatchRunId];
    if (!mf) return;
    const hasCurrent = mf.entries.some(
      (e) => e.metadata?.report_id === currentShareToken,
    );
    // `run` is written only once it is known to hold the token on screen, so
    // a reload (or a copied URL) reopens the panel on the same run.
    if (hasCurrent) {
      pendingRunNavigate.current = false;
      updateUrlRun(activeDispatchRunId);
      return;
    }
    if (pendingRunNavigate.current) {
      pendingRunNavigate.current = false;
      updateUrlRun(activeDispatchRunId);
      navigateToFirstDispatchEntry(mf);
    }
  }, [
    isShareFlow,
    activeDispatchRunId,
    dispatchManifests,
    currentShareToken,
    navigateToFirstDispatchEntry,
  ]);

  // The devnav panel should stay mounted while a newly-selected run's manifest
  // is loading, so we compute a "display manifest" that falls back to nothing
  // only when we genuinely have no data yet.
  const displayManifest = manifest;

  // When the panel has data and is flagged open, switch to phone view so the
  // user sees the narrative in a device frame alongside the panel.
  useEffect(() => {
    if (devnav && isDevNavOpen && displayManifest && !device) {
      handleSetDeviceType("phone");
    }
  }, [devnav, isDevNavOpen, displayManifest, device, handleSetDeviceType]);

  // Show admin page if ?admin param is present
  if (admin) {
    return <AdminPage />;
  }

  // Path-based use-case marketing pages (e.g. /use-cases/education).
  // Bypassed when ?report or ?share is present so deep links to narratives
  // hosted on the same domain continue to work.
  if (pathRoute?.kind === "use-case" && !reportId && !shareToken) {
    const config = getUseCaseConfig(pathRoute.slug);
    if (config) {
      return <UseCasePage config={config} />;
    }
  }

  // key={currentShareToken ?? currentReportId} forces NarrativeApp to remount on switch
  return (
    <>
      {/* DevNavigator toggle button (visible when panel is closed) */}
      {devnav && displayManifest && !isDevNavOpen && (
        <DevNavigatorToggle
          count={navigableCount}
          onClick={() => handleSetDevNavOpen(true)}
        />
      )}

      <div
        className={
          devnav && displayManifest && isDevNavOpen
            ? "flex h-[100dvh] w-full"
            : ""
        }
      >
        {/* DevNavigator side panel — lives outside the keyed NarrativeApp so it never remounts */}
        {devnav && displayManifest && isDevNavOpen && currentDevNavId && (
          <DevNavigator
            currentReportId={currentDevNavId}
            manifest={displayManifest}
            allManifests={allManifests}
            onNavigate={handleNavigate}
            onManifestChange={handleManifestChange}
            onClose={() => handleSetDevNavOpen(false)}
            deviceType={deviceType}
            onDeviceTypeChange={handleSetDeviceType}
            searchQuery={devNavSearch}
            onSearchChange={setDevNavSearch}
            isManifestLoading={isManifestLoading}
            manifestError={activeManifestError}
            manifestWarning={activeManifestWarning}
            onRetryManifest={handleRetryManifest}
            workspaceOptions={isShareFlow ? dispatchWorkspaces : undefined}
            activeWorkspaceId={activeWorkspaceId}
            onWorkspaceChange={setActiveWorkspaceId}
            onEmailExport={() => setShowEmailExport(true)}
          />
        )}

        <div
          className={
            devnav && displayManifest && isDevNavOpen ? "flex-1 min-w-0" : ""
          }
        >
          <NarrativeApp
            key={currentShareToken ?? currentReportId}
            reportId={currentReportId}
            shareToken={currentShareToken}
            page={page}
            format={format}
            videoDelayMs={videoDelayMs}
            token={token}
            devnav={shareViaDispatchSession}
            deviceType={deviceType}
            showEmailExport={showEmailExport}
            onEmailExportClose={() => setShowEmailExport(false)}
          />
        </div>
      </div>
    </>
  );
}

export default App;
